import { ChevronsLeft, Loader2 } from 'lucide-react';
import { useMemo } from 'react';

import { AtlasIcon, OrgLogoId, OrgLogos } from '@crossnokaye/ux-animatics';
import { cn, HeaderWithIcon } from '@crossnokaye/ux-common';

import { NavOptions } from '@/components/navigation/constants';
import { AccessSelectorContent } from '@/components/navigation/left/components/shared/access-selector-content';
import { NavTooltipContent } from '@/components/navigation/left/components/shared/nav-tooltip-content';
import { SquareButton } from '@/components/navigation/left/components/shared/square-button';
import { useAgentProxyMode } from '@/hooks/use-agent-proxy-mode';
import { useNavStore } from '@/stores/nav';
import { Tooltip, TooltipTrigger } from '@/ux-common/components/tooltip/tooltip';

import { ExpandedAccessSelector } from './expanded-access-selector';
import { ExpandedMainNav } from './expanded-main-nav';

export const ExpandedNav = ({
  onSelect,
  onSignOut,
  onToggleActive,
  switchToFacilityMenu,
  toggleExpanded,
  isViewOnly,
}: {
  onSelect: (id: string) => void;
  onSignOut: () => void;
  onToggleActive: () => void;
  switchToFacilityMenu: () => void;
  toggleExpanded: () => void;
  isViewOnly: boolean;
}) => {
  const {
    userName,
    facilityName,
    orgName,
    roles,
    orgIcon,
    enableFacilityMenu,
    userAccessIsLoading,
    activeNavMenu,
  } = useNavStore();

  const { isAgentProxyMode } = useAgentProxyMode();

  const handleSignOut = () => {
    if (!onSignOut) return;
    onSignOut();
  };

  const handleToggleActive = () => {
    if (!onToggleActive) return;
    onToggleActive();
  };

  // alphabetically sort roles, because the order of roles in the database is not guaranteed
  // and our roles happen to have alphabetical prevalence.  Ex. Admin > Blast > Viewer
  const topRole = useMemo(() => {
    return roles && roles.length ? [...roles].sort((a, b) => a.localeCompare(b))[0] : '';
  }, [roles]);

  const isLoading = !userName || !topRole || userAccessIsLoading;

  const OrgLogoIcon = OrgLogos[orgIcon as OrgLogoId];

  return (
    <div className={'h-full flex flex-col'} data-testid={'expanded-nav'}>
      <div className={'flex-shrink-0'}>
        <div
          className={'flex justify-between items-center py-4 px-3 cursor-pointer'}
          onClick={toggleExpanded}
          data-testid={'expanded-nav-logo'}
        >
          <AtlasIcon symbol={'atlasLogoFull'} width={'10rem'} height={'1.5rem'} />
          <Tooltip>
            <TooltipTrigger asChild>
              <SquareButton
                icon={<ChevronsLeft className="glyph glyph-sm" aria-hidden="true" />}
                // The whole logo header above also toggles on click; without
                // stopping propagation, one click on this button fired the
                // toggle twice — collapse then re-expand, a visible no-op.
                onClick={(event) => {
                  event.stopPropagation();
                  toggleExpanded();
                }}
                data-testid={'expanded-nav-collapse-toggle'}
              />
            </TooltipTrigger>
            <NavTooltipContent label={'Click to Collapse Menu'} />
          </Tooltip>
        </div>
        <div className={'mb-5'}>
          {orgIcon && orgName && facilityName ? (
            <Tooltip>
              <TooltipTrigger disabled={!enableFacilityMenu} className={'w-full'} asChild>
                <HeaderWithIcon
                  title={facilityName}
                  icon={
                    OrgLogoIcon ? (
                      <OrgLogoIcon
                        size={'2rem'}
                        className={'flex justify-center align-middle'}
                        color={'white'}
                      />
                    ) : null
                  }
                  subtitle={orgName}
                  onClick={enableFacilityMenu ? switchToFacilityMenu : undefined}
                  className={cn(
                    'text-foreground',
                    enableFacilityMenu && 'cursor-pointer hover:bg-tertiary'
                  )}
                  data-testid={'expanded-nav-org-header'}
                />
              </TooltipTrigger>
              <NavTooltipContent label={'Click to View Facility Picker'} />
            </Tooltip>
          ) : (
            <Loader2 className="glyph glyph-sm animate-spin" aria-label="Loading" />
          )}
        </div>
      </div>
      {activeNavMenu === NavOptions.Main ? <ExpandedMainNav onSelect={onSelect} /> : null}
      {!isAgentProxyMode && (
        <Tooltip>
          <TooltipTrigger asChild>
            <div className={'flex-shrink-0 mt-auto border-t border-border bg-secondary'}>
              <ExpandedAccessSelector
                isLoading={isLoading}
                isViewOnly={isViewOnly}
                name={userName ?? ''}
                role={topRole}
              >
                <AccessSelectorContent
                  isViewOnly={isViewOnly}
                  handleToggleActive={handleToggleActive}
                  handleSignOut={handleSignOut}
                />
              </ExpandedAccessSelector>
            </div>
          </TooltipTrigger>
          <NavTooltipContent label={'Click to View Access Options'} />
        </Tooltip>
      )}
    </div>
  );
};
